import { useInterval } from "@mantine/hooks";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { ScrollSync, ScrollSyncPane } from "react-scroll-sync";
import { calculateYOffsetForTime } from "../../../../../Utilities";
import {
  type CalendarDay,
  type CalendarLocation,
  type CalendarTimeOff,
  useCalendarStore,
} from "../../store/useCalendarStore";
import { getCalendarTimes } from "../../utils";
import { spansDate } from "../hooks/dayAbsence";
import DayColumn from "./DayColumn";
import DayName from "./DayName";
import { useShallow } from "zustand/react/shallow";
import { cn } from "@/utils";
import { Trans } from "@lingui/react/macro";

const DayWeekCalendar = ({
  highlightedDate,
}: {
  highlightedDate: string | null;
}) => {
  const { calendarData, filterItems, locations, timeoffs, selectedLocationId } =
    useCalendarStore(
      useShallow((state) => ({
        calendarData: state.calendarData,
        filterItems: state.filterItems,
        locations: state.locations,
        timeoffs: state.timeoffs,
        selectedLocationId: state.selectedLocationId,
      })),
    );

  const selectedLocationTimezone = useMemo(
    () =>
      locations.find(
        (location: CalendarLocation) =>
          location.locationId === selectedLocationId,
      )?.timeZone,
    [locations, selectedLocationId],
  );

  const nowAtLocation = useMemo(
    () =>
      selectedLocationTimezone ? dayjs().tz(selectedLocationTimezone) : dayjs(),
    [selectedLocationTimezone],
  );

  const [redLineData, setRedLineData] = useState({
    currentTime: nowAtLocation.format("HH:mm"),
    yOffset: 0,
  });

  const updateCurrentTimeLineData = () => {
    const currentDateTime = selectedLocationTimezone
      ? dayjs().tz(selectedLocationTimezone)
      : dayjs();
    const currentTime = currentDateTime.format("HH:mm");
    const newYoffset = calculateYOffsetForTime({
      time: currentTime,
      date: currentDateTime.format("YYYY-MM-DD"),
      timeZone: selectedLocationTimezone,
      slotHeight: calendarData.slotHeight,
      startTime: calendarData.startTime,
      endTime: calendarData.endTime,
      timeStep: calendarData.timeStep,
    });
    setRedLineData({
      currentTime,
      yOffset: newYoffset,
    });
  };

  useEffect(() => {
    updateCurrentTimeLineData();
  }, [filterItems, selectedLocationTimezone]);

  const redLineInterval = useInterval(() => {
    updateCurrentTimeLineData();
  }, 60000);

  const { startTime, endTime, timeStep, slotHeight } = calendarData;
  const times = useMemo(
    () => getCalendarTimes(startTime, endTime, timeStep),
    [startTime, endTime, timeStep],
  );

  useEffect(() => {
    updateCurrentTimeLineData();
    redLineInterval.start();
    return redLineInterval.stop;
  }, [calendarData, selectedLocationTimezone]);

  const isOnlyDay = calendarData.days?.length === 1;

  const isNoDays = calendarData.days?.length === 0;

  return (
    <>
      <ScrollSync>
        <>
          <div className="top_row empty"></div>
          <ScrollSyncPane>
            <div className="top_row scroll_row" id="users_row">
              <div
                className={cn("flex flex-row", {
                  "w-fit md:w-auto": !isOnlyDay,
                })}
              >
                {calendarData?.days?.map((day: CalendarDay, index: number) => (
                  <DayName day={day} key={day.date ?? index} />
                ))}
              </div>
            </div>
          </ScrollSyncPane>

          <div className="times_row">
            <div
              className="sticky left-0 z-3 flex w-fit flex-col bg-white pl-[5px] dark:bg-neutral-900"
              style={{
                gap: `max(calc(${slotHeight}px - 20px), 0px)`,
              }}
            >
              {times.map((time, index) => (
                <time
                  className="h-5 pr-1 text-xs font-medium text-gray-500"
                  key={index}
                >
                  {time}
                </time>
              ))}
            </div>
          </div>

          {isNoDays ? (
            <div className="flex h-full w-full items-start justify-center">
              <p className="text-gray-500">
                <Trans>Dan ni prikazan. Vklopite ga v filtru.</Trans>
              </p>
            </div>
          ) : null}

          <ScrollSyncPane>
            <div
              style={{ paddingTop: "7px", position: "relative" }}
              className={"calendar_row scroll_row"}
              id={"calendar_row"}
            >
              <div className="flex flex-1 flex-row">
                {calendarData?.days?.map((day: CalendarDay, index: number) => {
                  const isToday = dayjs(day.date).isSame(nowAtLocation, "day");
                  const shouldHighlight =
                    highlightedDate &&
                    dayjs(day.date).isSame(dayjs(highlightedDate), "day");
                  return (
                    <DayColumn
                      key={index}
                      day={day}
                      timeoffs={
                        timeoffs?.timeoffs?.filter((t: CalendarTimeOff) =>
                          spansDate(t.timeFrom, t.timeTo, day.date),
                        ) || []
                      }
                      isOnlyDay={calendarData.days.length === 1}
                      redLineData={isToday ? redLineData : null}
                      shouldHighlight={Boolean(shouldHighlight)}
                    />
                  );
                })}
              </div>
            </div>
          </ScrollSyncPane>
        </>
      </ScrollSync>
    </>
  );
};

export default DayWeekCalendar;
